import { useCallback, useRef, useEffect, useState } from 'react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
}

// Validate hex color string
function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

export default function ColorPicker({ value, onChange }: ColorPickerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  // Draw the spectrum when popover opens
  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Draw hue gradient (horizontal)
    const hueGradient = ctx.createLinearGradient(0, 0, width, 0);
    hueGradient.addColorStop(0, '#ff0000');
    hueGradient.addColorStop(0.17, '#ffff00');
    hueGradient.addColorStop(0.33, '#00ff00');
    hueGradient.addColorStop(0.5, '#00ffff');
    hueGradient.addColorStop(0.67, '#0000ff');
    hueGradient.addColorStop(0.83, '#ff00ff');
    hueGradient.addColorStop(1, '#ff0000');
    ctx.fillStyle = hueGradient;
    ctx.fillRect(0, 0, width, height);

    // Draw saturation gradient (white to transparent, left to right)
    const satGradient = ctx.createLinearGradient(0, 0, width, 0);
    satGradient.addColorStop(0, 'rgba(255,255,255,1)');
    satGradient.addColorStop(0.5, 'rgba(255,255,255,0)');
    ctx.fillStyle = satGradient;
    ctx.fillRect(0, 0, width, height);

    // Draw lightness gradient (black from bottom)
    const lightGradient = ctx.createLinearGradient(0, 0, 0, height);
    lightGradient.addColorStop(0, 'rgba(0,0,0,0)');
    lightGradient.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = lightGradient;
    ctx.fillRect(0, 0, width, height);
  }, [isOpen]);

  // Close popover when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    // Delay to prevent immediate close from the opening click
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getColorAtPosition = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return value;

    const ctx = canvas.getContext('2d');
    if (!ctx) return value;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const canvasX = Math.max(0, Math.min(canvas.width - 1, (x - rect.left) * scaleX));
    const canvasY = Math.max(0, Math.min(canvas.height - 1, (y - rect.top) * scaleY));

    const imageData = ctx.getImageData(canvasX, canvasY, 1, 1).data;
    const hex = '#' + [imageData[0], imageData[1], imageData[2]]
      .map(c => c.toString(16).padStart(2, '0'))
      .join('');
    return hex;
  }, [value]);

  const handleInteraction = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const color = getColorAtPosition(clientX, clientY);
    onChange(color);
  }, [getColorAtPosition, onChange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    handleInteraction(e);
  }, [handleInteraction]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      handleInteraction(e);
    }
  }, [isDragging, handleInteraction]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    handleInteraction(e);
  }, [handleInteraction]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isDragging) {
      handleInteraction(e);
    }
  }, [isDragging, handleInteraction]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global mouse up handler
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  // Sync input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // Add # if not present
    if (newValue && !newValue.startsWith('#')) {
      newValue = '#' + newValue;
    }

    setInputValue(newValue);

    // Only update if valid hex
    if (isValidHex(newValue)) {
      onChange(newValue);
    }
  }, [onChange]);

  const handleInputBlur = useCallback(() => {
    // Reset to current value if invalid
    if (!isValidHex(inputValue)) {
      setInputValue(value);
    }
  }, [inputValue, value]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Color swatch and input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          background: 'transparent',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            backgroundColor: value,
            border: '1px solid var(--color-border)',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        />
        <input
          type="text"
          value={inputValue.toUpperCase()}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={() => setIsOpen(true)}
          placeholder="#FFFFFF"
          style={{
            flex: 1,
            padding: 0,
            border: 'none',
            fontFamily: "'Host Grotesk', sans-serif",
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            background: 'transparent',
            outline: 'none',
            minWidth: 0,
          }}
        />
      </div>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '10px',
            border: '1px solid var(--color-border)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 100,
          }}
        >
          <canvas
            ref={canvasRef}
            width={200}
            height={120}
            style={{
              width: '100%',
              height: '120px',
              borderRadius: '6px',
              cursor: 'crosshair',
              touchAction: 'none',
              display: 'block',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        </div>
      )}
    </div>
  );
}
